import React, { useState, useEffect, useCallback  } from 'react';
import { 
  Heart, MessageCircle, Eye, TrendingUp, Flame, Award, Users, Clock, 
  ChevronUp, ChevronDown, Share2, Bookmark, Filter, Search, Sparkles, 
  Zap, Star, Trophy, Gift, Bell, Plus, X, Edit3, Camera, MapPin, 
  Calendar, Hash, Leaf, Activity, BarChart3, Target, Crown, 
  Coffee, Moon, Sun, Lightbulb, Palette, Music, Save, User, MessageSquare,
  Globe, Lock, Eye as EyeIcon, EyeOff, BookOpen, Microscope, Beaker, Download, ArrowRight
} from 'lucide-react';
import { Grid } from '@mui/material';
import EditProfile from './EditPage';
import API_URL from '../config/ApiUrl';
import { AchievementsCard } from '../components/AchievementsCard';
import { UserLevelCard } from '../components/UserLevelCard';
import CoverImageUploadDialog from '../components/ImageUploadDialog';
import { useUser } from '../context/UserContext';
import AvatarOnboardingDialog from './AvatarSelectorDialog';
import EnhancedActivityCard from './EnhnacedProfileActivity';
import BackgroundColorSelector from './BackgroundProfileDialog';
import { useTheme } from '../arrangement/ThemeContext';
const ProfilePage = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [isEditing, setIsEditing] = useState(false);
  const [isFirstTime, setIsFirstTime] = useState(false);
  const [isMockProfile, setIsMockProfile] = useState(false);
  const [formData, setFormData] = useState({});
  const [errors, setErrors] = useState({});
  const [profileData, setProfileData] = useState(null);
  const [recentActivity, setRecentActivity] = useState([]);
  const [activityLoading, setActivityLoading] = useState(true);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const { isAuthenticated,  } = useUser();
  const { colors } = useTheme();

  const [achievementsLoading, setAchievementsLoading] = useState(true);
  
  const [activityLimit, setActivityLimit] = useState(8); // Start with 8 for overview
  const [activityOffset, setActivityOffset] = useState(0);
  const [allActivities, setAllActivities] = useState([]);
  const [hasMoreActivities, setHasMoreActivities] = useState(false);
  const [totalActivities, setTotalActivities] = useState(0);
  const [showAllMode, setShowAllMode] = useState(false);

  const [selectedColor, setSelectedColor] = useState(null);
  const [backgroundData, setBackgroundData] = useState(null);
  const [backgroundLoading, setBackgroundLoading] = useState(false);

  const [dialogOpen, setDialogOpen] = useState(false);

  const [avatarSelectorOpen, setAvatarSelectorOpen] = useState(false);

  // Mobile detection
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

const fetchAchievements = async () => {
  try {
    setAchievementsLoading(true);
    const response = await fetch(`${API_URL}/api/achievements`, {
      method: 'GET',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    const result = await response.json();
    if (result.success) {
      setProfileData(prev => ({
        ...prev,
        achievements: result.data.achievements || [],
        displayed_achievements: result.data.displayed_achievements || []
      }));
    }
  } catch (error) {
    console.error('Error fetching achievements:', error);
    // Don't reset achievements on error - keep existing ones
  } finally {
    setAchievementsLoading(false);
  }
};
  const fetchBackgroundData = async () => {
    try {
      const response = await fetch(`${API_URL}/api/profile/background`, {
        credentials: 'include',
      });
      const result = await response.json();
      if (result.success) {
        setBackgroundData(result.data);
        if (result.data.current_background) {
          setSelectedColor(result.data.current_background);
          setProfileData(prev => ({
            ...prev,
            background_gradient: result.data.current_background.gradient
          }));
        }
      }
    } catch (error) {
      console.error('Error fetching background data:', error);
    }
  };

  // Update your existing handleColorSelect function
  const handleColorSelect = async (color) => {
    try {
      setBackgroundLoading(true);
      
      const response = await fetch(`${API_URL}/api/profile/background`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          color_id: color.id,
          gradient: color.gradient
        })
      });

      const result = await response.json();
      
      if (result.success) {
        setSelectedColor(color);
        setDialogOpen(false);
        
        // Update profile data with new background
        setProfileData(prev => ({
          ...prev,
          background_gradient: color.gradient
        }));
      } else {
        alert(`Error: ${result.error}`);
      }
    } catch (error) {
      alert('Failed to update background. Please try again.');
    } finally {
      setBackgroundLoading(false);
    }
  };

  const handleAvatarSelect = (newAvatarUrl) => {
    setProfileData(prev => ({
      ...prev,
      user_avatar: newAvatarUrl
    }));
  };



  const getUserStats = () => {
    if (!profileData) return null;
    console.log('---------------++++++', profileData)
    return {
      level: profileData.level || 1,
      xp: profileData.xp || 0,
      nextLevelXp: profileData.next_level_xp || 100,
      streak: profileData.streak || 0,
      badges: profileData.badges ? profileData.badges.map(badge => 
        badge.icon ? `${badge.icon} ${badge.name || badge.type}` : (badge.name || badge.type)
      ) : [],
      reputation: profileData.reputation || 0,
      posts: profileData.total_posts || 0,
      comments: profileData.total_comments || 0,
      upvotes: profileData.total_upvotes || 0,
      // Additional stats from your backend
      threads: profileData.total_threads || 0,
      reviews: profileData.total_reviews || 0,
      helpful_answers: profileData.helpful_answers || 0,
      sesh_rooms_joined: profileData.sesh_rooms_joined || 0,
      next_level_threshold: profileData.next_level_threshold || profileData.next_level_xp || 100,
    };
  };

  const fetchProfile = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/profile`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const result = await response.json();
      
      if (result.success) {
        setProfileData(result.data);

        const isMock = result.is_mock || !result.data.display_name;

        
        setIsMockProfile(isMock);
        setIsFirstTime(isMock);
        setIsEditing(isMock);// Auto-enter edit mode for mock profiles
        
        // Initialize form data
        initializeFormData(result.data);
      } else {
        console.error('Failed to fetch profile:', result.error);
        // Handle error - maybe show error message
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
      // Handle network error
    } finally {
      setLoading(false);
    }
  };


  useEffect(() => {
    fetchProfile();
  }, []);


  useEffect(() => {
    if (profileData && !profileData.is_mock) {
      const userId = profileData.user_id;
      
      // Fetch user activity
      fetchUserActivity();
      
      // Fetch achievements - ADD THIS LINE
      fetchAchievements();
      
      // Only fetch background data if we don't have it already
      if (!backgroundData) {
        fetchBackgroundData();
      }
    }
  }, [profileData?.user_id]);

  
   const fetchUserActivity = async (limit = 10, offset = 0, append = false) => {
    try {
      setActivityLoading(true);

      const response = await fetch(
        `${API_URL}/api/user-activity/${profileData.user_id || 'me'}?limit=${limit}&offset=${offset}`, 
        {
          method: 'GET',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      const result = await response.json();
      
      if (result.success) {
        if (append) {
          // Append to existing activities for "Load More"
          setAllActivities(prev => [...prev, ...result.data]);
        } else {
          // Replace activities for new limit or initial load
          setAllActivities(result.data);
          setRecentActivity(result.data); // Keep this for backward compatibility
        }
        
        setHasMoreActivities(result.has_more || false);
        setTotalActivities(result.total || 0);
        
      } else {
        console.error('Failed to fetch user activity:', result.error);
        setAllActivities([]);
        setRecentActivity([]);
      }
    } catch (error) {
      console.error('Error fetching user activity:', error);
      setAllActivities([]);
      setRecentActivity([]);
    } finally {
      setActivityLoading(false);
    }
  };


  const userStats = getUserStats();

  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffMinutes = Math.floor(diffMs / (1000 * 60));

    if (diffDays > 0) {
      return `${diffDays} day${diffDays === 1 ? '' : 's'} ago`;
    } else if (diffHours > 0) {
      return `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`;
    } else if (diffMinutes > 0) {
      return `${diffMinutes} minute${diffMinutes === 1 ? '' : 's'} ago`;
    } else {
      return 'Just now';
    }
  };

  const getActivityTypeInfo = (type) => {
    switch (type) {
      case 'thread':
        return {
          icon: <MessageSquare size={14} />,
          color: '#8b5cf6',
          bgColor: '#8b5cf620',
          borderColor: '#8b5cf640',
          label: 'Thread'
        };
      case 'comment':
        return {
          icon: <MessageCircle size={14} />,
          color: '#00d4aa',
          bgColor: '#00d4aa20',
          borderColor: '#00d4aa40',
          label: 'Comment'
        };
      case 'research_article':
        return {
          icon: <BookOpen size={14} />,
          color: '#3b82f6',
          bgColor: '#3b82f620',
          borderColor: '#3b82f640',
          label: 'Research Article'
        };
      case 'article_discussion':
        return {
          icon: <Microscope size={14} />,
          color: '#f59e0b',
          bgColor: '#f59e0b20',
          borderColor: '#f59e0b40',
          label: 'Article Discussion'
        };
      case 'poll':
        return {
          icon: <BarChart3 size={14} />,
          color: '#ef4444',
          bgColor: '#ef444420',
          borderColor: '#ef444440',
          label: 'Poll'
        };
      default:
        return {
          icon: <Activity size={14} />,
          color: '#666',
          bgColor: '#66620',
          borderColor: '#66640',
          label: 'Activity'
        };
    }
  };
  const handleLimitChange = (newLimit) => {
      setActivityLimit(newLimit);
      setActivityOffset(0);
      setShowAllMode(true);
      fetchUserActivity(newLimit, 0, false);
    };

    const handleLoadMore = () => {
      const newOffset = activityOffset + activityLimit;
      setActivityOffset(newOffset);
      fetchUserActivity(activityLimit, newOffset, true);
    };

    const handleViewAllActivity = () => {
      setShowAllMode(true);
      if (allActivities.length <= 8) {
        handleLimitChange(15);
      }
    };

    const handleBackToOverview = () => {
      setShowAllMode(false);
      setActivityOffset(0);
      setActivityLimit(8);
      fetchUserActivity(8, 0, false);
    };



  const initializeFormData = (data) => {
    console.log('----------------DATA: ', data)
      const defaultDisplayName = (!data.display_name && data.username) 
    ? data.username 
    : (data.display_name || '');
    setFormData({
      displayName: defaultDisplayName,
      bio: data.bio || '',
      username:data.username|| 'username',
      location: data.location || '',
      website: data.website || '',
      favoriteStrains: data.favorite_strains || [],
      consumptionMethods: data.consumption_methods || [],
      toleranceLevel: data.tolerance_level || 'medium',
      yearsOfExperience: data.years_of_experience || 1,
      medicalUser: data.medical_user || false,
      isPublic: data.is_public !== false,
      showLocation: data.show_location !== false,
      showMedicalStatus: data.show_medical_status !== false,
      showConsumptionMethods: data.show_consumption_methods !== false
    });
  };

  const strainOptions = [
    "Blue Dream", "Sour Diesel", "Girl Scout Cookies", "OG Kush", "White Widow",
    "AK-47", "Purple Haze", "Jack Herer", "Granddaddy Purple", "Northern Lights",
    "Green Crack", "Pineapple Express", "Strawberry Cough", "Afghani", "Bubba Kush"
  ];

  const consumptionOptions = [
    "Joints", "Blunts", "Pipes", "Bongs", "Vaporizer", "Edibles", 
    "Tinctures", "Topicals", "Dabs", "Concentrates"
  ];

  const toleranceLevels = [
    { value: "low", label: "Low - New to cannabis" },
    { value: "medium", label: "Medium - Regular user" },
    { value: "high", label: "High - Daily user" }
  ];

  const handleInputChange = useCallback((field, value) => {

    setFormData(prev => {
      console.log('🔥 setFormData prev:', prev);
      const newData = { ...prev, [field]: value };
      console.log('🔥 setFormData new:', newData);
      return newData;
    });
    
    setErrors(prev => {
      console.log('🔥 setErrors called for field:', field);
      if (prev[field]) {
        return { ...prev, [field]: null };
      }
      return prev;
    });
  }, []);

  const handleArrayAdd = (field, value) => {
    if (value && !formData[field].includes(value)) {
      handleInputChange(field, [...formData[field], value]);
    }
  };

  const handleArrayRemove = (field, value) => {
    handleInputChange(field, formData[field].filter(item => item !== value));
  };

  const handleCoverImageSuccess = (newImageUrl) => {
    setProfileData(prev => ({
      ...prev,
      cover_image_url: newImageUrl
    }));
    // Optionally refetch profile data
    // fetchProfile();
  };

  const getSmokerEmoji = (level) => {
    switch(level) {
      case 'beginner': return 'https://highmind-bucket.s3.us-east-1.amazonaws.com/cdb53b7a8c7e4624a810c0d654ca03c8_Smoker_beginer.webp';
      case 'casual': return 'https://highmind-bucket.s3.us-east-1.amazonaws.com/e72017f33d1c480db1928ca3d7295de4_Smoker_Level_cas.webp';
      case 'connoisseur': return 'https://highmind-bucket.s3.us-east-1.amazonaws.com/3e1e13711dd441fcb7c34c155416ee69_Smoker_Level_con.webp';
      case 'stoner': return 'https://highmind-bucket.s3.us-east-1.amazonaws.com/9084554e27d747188148530bc7899eee_Smoker_Level_Logo_Stoner.webp';
      default: return 'https://highmind-bucket.s3.us-east-1.amazonaws.com/6d4ba06007624c3691fd682dfbb31cdf_hmind.png'
    }
  };


  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.displayName?.trim()) {
      newErrors.displayName = "Display name is required";
    }
    
    if (!formData.bio?.trim()) {
      newErrors.bio = "Bio is required";
    } else if (formData.bio.length > 500) {
      newErrors.bio = "Bio must be under 500 characters";
    }
    
    if (formData.favoriteStrains.length === 0) {
      newErrors.favoriteStrains = "Please select at least one favorite strain";
    }
    
    if (formData.consumptionMethods.length === 0) {
      newErrors.consumptionMethods = "Please select at least one consumption method";
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;
    
    try {
      setLoading(true);
      
      const apiUrl = isMockProfile ? `${API_URL}/api/profile` : `${API_URL}/api/profile`;
      const method = isMockProfile ? 'POST' : 'PUT';
      
      const response = await fetch(apiUrl, {
        method: method,
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          display_name: formData.displayName,
          bio: formData.bio,
          location: formData.location,
          website: formData.website,
          favorite_strains: formData.favoriteStrains,
          consumption_methods: formData.consumptionMethods,
          tolerance_level: formData.toleranceLevel,
          years_of_experience: formData.yearsOfExperience,
          medical_user: formData.medicalUser,
          is_public: formData.isPublic,
          show_location: formData.showLocation,
          show_medical_status: formData.showMedicalStatus,
          show_consumption_methods: formData.showConsumptionMethods
        })
      });

      const result = await response.json();
      
      if (result.success) {
        // Update local state
        setProfileData(result.data);
        setIsMockProfile(false);
        setIsEditing(false);
        setIsFirstTime(false);
        
        // Show success message
        alert(result.message || 'Profile saved successfully!');
      } else {
        // Handle validation errors
        if (result.error) {
          alert(`Error: ${result.error}`);
        }
      }
      
    } catch (error) {
      console.error('Failed to save profile:', error);
      alert("Failed to save profile. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    if (isMockProfile) {
      // Don't allow canceling if it's a mock profile
      return;
    }
    
    // Reset form data to original profile data
    initializeFormData(profileData);
    setIsEditing(false);
    setErrors({});
  };

  console.log("FORM DATYA IS HEREL : ", formData)
  const FormField = ({ label, error, children, required = false }) => (
    <div style={{ marginBottom: '20px' }}>
      <label style={{
        display: 'block',
        fontSize: '14px',
        fontWeight: '600',
        color: '#e5e5e5',
        marginBottom: '8px'
      }}>
        {label} {required && <span style={{ color: '#ef4444' }}>*</span>}
      </label>
      {children}
      {error && (
        <p style={{
          margin: '4px 0 0 0',
          fontSize: '12px',
          color: '#ef4444'
        }}>
          {error}
        </p>
      )}
    </div>
  );

      const MultiSelect = ({ options, selected, onAdd, onRemove, placeholder }) => {
    const [inputValue, setInputValue] = useState('');
    const [showDropdown, setShowDropdown] = useState(false);
    
    const filteredOptions = options.filter(option => 
      !selected.includes(option) && 
      option.toLowerCase().includes(inputValue.toLowerCase())
    );

    return (
      <div style={{ position: 'relative' }}>
        {/* Selected items */}
        <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '8px',
          marginBottom: '8px'
        }}>
          {selected.map((item, index) => (
            <span key={index} style={{
              padding: '4px 8px',
              background: 'linear-gradient(135deg, #00d4aa 0%, #10b981 100%)',
              borderRadius: '8px',
              fontSize: '12px',
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              gap: '4px'
            }}>
              {item}
              <button
                onClick={() => onRemove(item)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'white',
                  cursor: 'pointer',
                  padding: '0',
                  width: '16px',
                  height: '16px',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <X size={10} />
              </button>
            </span>
          ))}
        </div>

        {/* Input field */}
        <input
          type="text"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onFocus={() => setShowDropdown(true)}
          onBlur={() => setTimeout(() => setShowDropdown(false), 200)}
          placeholder={placeholder}
          style={{
            width: '100%',
            padding: '12px',
            background: colors.bgCard,
            border: '2px solid #333',
            borderRadius: '12px',
            color: '#e5e5e5',
            fontSize: '14px'
          }}
        />

        {/* Dropdown */}
        {showDropdown && filteredOptions.length > 0 && (
          <div style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            background: colors.bgCard,
            border: '2px solid #333',
            borderTop: 'none',
            borderRadius: '0 0 12px 12px',
            maxHeight: '200px',
            overflowY: 'auto',
            zIndex: 1000
          }}>
            {filteredOptions.map((option, index) => (
              <button
                key={index}
                onClick={() => {
                  onAdd(option);
                  setInputValue('');
                }}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  background: 'none',
                  border: 'none',
                  color: '#e5e5e5',
                  textAlign: 'left',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
                onMouseEnter={(e) => e.target.style.background = '#333'}
                onMouseLeave={(e) => e.target.style.background = 'none'}
              >
                {option}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  };

 const ProfileHeader = () => (
  <div style={{
    position: 'relative',
    background: colors.bgTertiary,
    borderRadius: isMobile ? '16px' : '24px',
    overflow: 'hidden',
    border: '1px solid #333',
    marginBottom: isMobile ? '20px' : '32px'
  }}>
    {/* Cover Photo */}
    <div style={{
      height: isMobile ? '120px' : '200px',
      background: profileData.background_gradient || 
        'linear-gradient(135deg, rgba(0, 212, 170, 0.8) 0%, rgba(16, 185, 129, 0.8) 100%)',
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      position: 'relative'
    }}>
      <button 
        onClick={() => setDialogOpen(true)}
        disabled={backgroundLoading}
        style={{
          position: 'absolute',
          top: isMobile ? '8px' : '16px',
          right: isMobile ? '8px' : '16px',
          padding: isMobile ? '6px 8px' : '8px 12px',
          background: 'rgba(0, 0, 0, 0.5)',
          border: 'none',
          borderRadius: isMobile ? '8px' : '12px',
          color: 'white',
          cursor: backgroundLoading ? 'not-allowed' : 'pointer',
          backdropFilter: 'blur(10px)',
          fontSize: isMobile ? '10px' : '12px',
          fontWeight: '500',
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          opacity: backgroundLoading ? 0.6 : 1
        }}
      >
        <Palette size={isMobile ? 12 : 14} />
        {backgroundLoading ? 'Updating...' : (isMobile ? 'Change BG' : 'Change Background')}
      </button>
    </div>

    {/* Profile Info */}
    <div style={{ padding: isMobile ? '16px' : '24px' }}>
      <div style={{ 
        display: 'flex', 
        alignItems: 'flex-start', 
        gap: isMobile ? '12px' : '24px', 
        marginTop: isMobile ? '-40px' : '-60px',
        flexDirection: isMobile ? 'column' : 'row'
      }}>
        {/* Avatar Section */}
        <div style={{ 
          display: 'flex', 
          flexDirection: isMobile ? 'row' : 'column', 
          alignItems: 'center', 
          gap: isMobile ? '12px' : '16px',
          width: isMobile ? '100%' : 'auto'
        }}>
          {/* Main Profile Avatar */}
          <div style={{ position: 'relative' }}>
            <img
              src={profileData.avatar}
              alt={profileData.username}
              style={{
                width: isMobile ? '80px' : '120px',
                height: isMobile ? '80px' : '120px',
                borderRadius: isMobile ? '16px' : '24px',
                border: '4px solid #1a1a1a',
                objectFit: 'cover'
              }}
            />
            <button 
            onClick={() => setUploadDialogOpen(true)}
            style={{
              position: 'absolute',
              bottom: isMobile ? '4px' : '8px',
              right: isMobile ? '4px' : '8px',
              width: isMobile ? '24px' : '32px',
              height: isMobile ? '24px' : '32px',
              background: 'linear-gradient(135deg, #00d4aa 0%, #10b981 100%)',
              border: 'none',
              borderRadius: '50%',
              color: 'white',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <Camera size={isMobile ? 10 : 14} />
            </button>
          </div>

          {/* Default User Avatar */}
          <div 
              style={{ 
                position: 'relative', 
                cursor: 'pointer',
                transition: 'all 0.3s ease'
              }}
              onClick={() => setAvatarSelectorOpen(true)}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'scale(1.05)';
                e.currentTarget.style.filter = 'brightness(1.1)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'scale(1)';
                e.currentTarget.style.filter = 'brightness(1)';
              }}
            >
              <img
                src={profileData.user_avatar || "https://highmind-bucket.s3.us-east-1.amazonaws.com/6d4ba06007624c3691fd682dfbb31cdf_hmind.png"}
                alt="User avatar - Click to change"
                style={{
                  width: isMobile ? '40px' : '60px',
                  height: isMobile ? '40px' : '60px',
                  borderRadius: isMobile ? '8px' : '12px',
                  border: '2px solid #333',
                  objectFit: 'cover',
                  transition: 'all 0.3s ease'
                }}
              />
              {/* Hover indicator */}
              <div style={{
                position: 'absolute',
                top: '2px',
                right: '2px',
                width: isMobile ? '12px' : '16px',
                height: isMobile ? '12px' : '16px',
                background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                opacity: 0.8
              }}>
                <Edit3 size={isMobile ? 6 : 8} color="white" />
              </div>
            </div>
          </div>

        {/* User Info and Level Card Container */}
        <Grid container spacing={0} alignItems="flex-start" sx={{ 
          margin: 0, 
          width: '100%', 
          paddingTop: isMobile ? '0px' : '60px',
          flexDirection: isMobile ? 'column' : 'row'
        }}>
          <Grid item xs sx={{ padding: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px', flexWrap: isMobile ? 'wrap' : 'nowrap' }}>
              <h1 style={{
                margin: 0,
                fontSize: isMobile ? '20px' : '28px',
                fontWeight: '700',
                color: '#e5e5e5'
              }}>
                {profileData.display_name}
                {isMockProfile && (
                  <span style={{
                    fontSize: isMobile ? '12px' : '16px',
                    color: '#ef4444',
                    fontWeight: '500',
                    marginLeft: '8px'
                  }}>
                    (Preview)
                  </span>
                )}
              </h1>
              {profileData.verified && !isMockProfile && (
                <div style={{
                  background: 'linear-gradient(135deg, #00d4aa 0%, #10b981 100%)',
                  borderRadius: '50%',
                  width: isMobile ? '20px' : '24px',
                  height: isMobile ? '20px' : '24px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  ✓
                </div>
              )}
              <div style={{
                position: 'relative',
                display: 'inline-block'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <div style={{
                    padding: isMobile ? '2px 8px' : '4px 12px',
                    background: 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)',
                    borderRadius: isMobile ? '8px' : '12px',
                    fontSize: isMobile ? '10px' : '12px',
                    fontWeight: '600',
                    color: 'white',
                    textTransform: 'capitalize'
                  }}>
                    {profileData.smoker_level}
                  </div>
                  
                  <img 
                    src={getSmokerEmoji(profileData.smoker_level)}
                    alt={`${profileData.smoker_level} level`}
                    style={{
                    backgroundColor: '#111',
                    borderRadius: '50%',
                    width: isMobile ? '32px' : '44px',
                    height: isMobile ? '32px' : '44px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: isMobile ? '8px' : '10px',
                    border: '1px solid #00d4aa'
                  }}>
                  </img>
                </div>
              </div>
              
            </div>

            <p style={{
              margin: '0 0 8px 0',
              fontSize: isMobile ? '14px' : '16px',
              color: '#888'
            }}>
              @{profileData.username}
            </p>

            <p style={{
              margin: '0 0 16px 0',
              fontSize: isMobile ? '14px' : '16px',
              color: '#bbb',
              lineHeight: '1.5',
              maxWidth: isMobile ? '100%' : '600px'
            }}>
              {profileData.bio}
            </p>

            <div style={{ 
              display: 'flex', 
              flexWrap: 'wrap', 
              gap: isMobile ? '12px' : '16px', 
              marginBottom: '16px',
              flexDirection: isMobile ? 'column' : 'row'
            }}>
              {(profileData.show_location || profileData.is_public) && profileData.location && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#888' }}>
                  <MapPin size={isMobile ? 12 : 14} />
                  <span style={{ fontSize: isMobile ? '12px' : '14px' }}>{profileData.location}</span>
                </div>
              )}
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#888' }}>
                <Calendar size={isMobile ? 12 : 14} />
                <span style={{ fontSize: isMobile ? '12px' : '14px' }}>Joined {new Date(profileData.joined_date).toLocaleDateString()}</span>
              </div>
              {(profileData.show_medical_status || profileData.is_public) && profileData.medical_user && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#10b981' }}>
                  <Plus size={isMobile ? 12 : 14} />
                  <span style={{ fontSize: isMobile ? '12px' : '14px', fontWeight: '500' }}>Medical User</span>
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div style={{ 
              display: 'flex', 
              gap: isMobile ? '8px' : '12px',
              flexDirection: isMobile ? 'column' : 'row',
              width: isMobile ? '100%' : 'auto'
            }}>
              <button
                onClick={() => setIsEditing(true)}
                style={{
                  padding: isMobile ? '10px 16px' : '12px 24px',
                  background: 'linear-gradient(135deg, #00d4aa 0%, #10b981 100%)',
                  color: 'white',
                  border: 'none',
                  borderRadius: isMobile ? '8px' : '12px',
                  cursor: 'pointer',
                  fontSize: isMobile ? '12px' : '14px',
                  fontWeight: '600',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  width: isMobile ? '100%' : 'auto'
                }}
              >
                <Edit3 size={isMobile ? 14 : 16} />
                Edit Profile
              </button>
              <button style={{
                padding: isMobile ? '10px 16px' : '12px 24px',
                background: 'transparent',
                color: '#888',
                border: '2px solid #333',
                borderRadius: isMobile ? '8px' : '12px',
                cursor: 'pointer',
                fontSize: isMobile ? '12px' : '14px',
                fontWeight: '600',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                width: isMobile ? '100%' : 'auto'
              }}>
                <Share2 size={isMobile ? 14 : 16} />
                Share Profile
              </button>
            </div>
          </Grid>
          
          {!isMobile && (
            <Grid item style={{ padding: 0 }}>
              <div style={{ 
                position: 'absolute',
                right: '24px'
              }}>
                <UserLevelCard stats={userStats} isAuthenticated={isAuthenticated} />
              </div>
            </Grid>
          )}
        </Grid>
      </div>
      
      {/* Mobile Level Card - Show below profile info on mobile */}
      {isMobile && (
        <div style={{ marginTop: '20px' }}>
          <UserLevelCard stats={userStats} isAuthenticated={isAuthenticated} />
        </div>
      )}
    </div>
  </div>
);
  const stats = profileData ? [
    { label: "Threads", value: profileData.total_threads || 0, icon: <MessageSquare size={16} />, color: "#8b5cf6" },
    { label: "Posts", value: profileData.total_posts || 0, icon: <Edit3 size={16} />, color: "#00d4aa" },
    { label: "Comments", value: profileData.total_comments || 0, icon: <MessageCircle size={16} />, color: "#10b981" },
    { label: "Reviews", value: profileData.total_reviews || 0, icon: <Star size={16} />, color: "#fbbf24" },
    { label: "Upvotes", value: profileData.total_upvotes || 0, icon: <Heart size={16} />, color: "#ef4444" },
    { label: "Discussion based", value: profileData.helpful_answers || 0, icon: <Award size={16} />, color: "#f59e0b" }
  ] : [];

  const StatsCard = () => (
    <div style={{
      background: colors.bgTertiary,
      borderRadius: isMobile ? '16px' : '20px',
      padding: isMobile ? '16px' : '24px',
      border: '1px solid #333',
      marginBottom: isMobile ? '16px' : '24px'
    }}>
      <h3 style={{
        margin: '0 0 20px 0',
        fontSize: isMobile ? '16px' : '18px',
        fontWeight: '600',
        color: '#e5e5e5',
        display: 'flex',
        alignItems: 'center',
        gap: '8px'
      }}>
        <BarChart3 size={isMobile ? 16 : 18} style={{ color: '#8b5cf6' }} />
        Community Stats
      </h3>
      
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)', 
        gap: isMobile ? '12px' : '16px' 
      }}>
        {stats.map((stat, index) => (
          <div key={index} style={{
            padding: isMobile ? '12px' : '16px',
            background: 'linear-gradient(135deg, #2a2a2a 0%, #1f1f1f 100%)',
            borderRadius: isMobile ? '12px' : '16px',
            border: '1px solid #333',
            textAlign: 'center',
            transition: 'all 0.3s ease',
            cursor: 'pointer'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.borderColor = stat.color;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.borderColor = '#333';
          }}
          >
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              marginBottom: '8px',
              color: stat.color
            }}>
              {React.cloneElement(stat.icon, { size: isMobile ? 14 : 16 })}
            </div>
            <div style={{
              fontSize: isMobile ? '20px' : '24px',
              fontWeight: '700',
              color: '#e5e5e5',
              marginBottom: '4px'
            }}>
              {stat.value}
            </div>
            <div style={{
              fontSize: isMobile ? '10px' : '12px',
              color: '#888',
              fontWeight: '500'
            }}>
              {stat.label}
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const BadgesCard = () => (
    <div style={{
      background: colors.bgTertiary,
      borderRadius: isMobile ? '16px' : '20px',
      padding: isMobile ? '16px' : '20px',
      border: '1px solid #333',
      marginBottom: isMobile ? '16px' : '24px'
    }}>
      <h3 style={{
        margin: '0 0 16px 0',
        fontSize: isMobile ? '14px' : '16px',
        fontWeight: '600',
        color: '#e5e5e5',
        display: 'flex',
        alignItems: 'center',
        gap: '8px'
      }}>
        <Award size={isMobile ? 14 : 16} style={{ color: '#fbbf24' }} />
        Badges & Achievements
      </h3>
      
      {profileData.badges && profileData.badges.length > 0 ? (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
          {profileData.badges.map((badge, index) => (
            <div key={index} style={{
              padding: isMobile ? '6px 10px' : '8px 12px',
              background: `${badge.color || '#00d4aa'}20`,
              borderRadius: isMobile ? '8px' : '12px',
              fontSize: isMobile ? '10px' : '12px',
              fontWeight: '600',
              color: badge.color || '#00d4aa',
              border: `1px solid ${badge.color || '#00d4aa'}40`,
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              display: 'flex',
              alignItems: 'center',
              gap: '4px'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = `${badge.color || '#00d4aa'}30`;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = `${badge.color || '#00d4aa'}20`;
            }}
            title={badge.description}
            >
              {badge.icon && <span>{badge.icon}</span>}
              <span>{badge.name || badge.type}</span>
            </div>
          ))}
        </div>
      ) : (
        <div style={{
          textAlign: 'center',
          padding: isMobile ? '16px' : '24px 16px',
          color: '#888'
        }}>
          <Award size={isMobile ? 24 : 32} style={{ marginBottom: '12px', opacity: 0.5 }} />
          <p style={{ margin: 0, fontSize: isMobile ? '12px' : '14px' }}>
            No badges earned yet
          </p>
          <p style={{ margin: '4px 0 0 0', fontSize: isMobile ? '10px' : '12px' }}>
            Stay active to earn your first badge!
          </p>
        </div>
      )}
    </div>
  );

  const PreferencesCard = () => (
    <div style={{
      background: colors.bgTertiary,
      borderRadius: isMobile ? '16px' : '20px',
      padding: isMobile ? '16px' : '20px',
      border: '1px solid #333',
      marginBottom: isMobile ? '16px' : '24px'
    }}>
      <h3 style={{
        margin: '0 0 16px 0',
        fontSize: isMobile ? '14px' : '16px',
        fontWeight: '600',
        color: '#e5e5e5',
        display: 'flex',
        alignItems: 'center',
        gap: '8px'
      }}>
        <Leaf size={isMobile ? 14 : 16} style={{ color: '#10b981' }} />
        Cannabis Preferences
      </h3>
      
      {(profileData.show_consumption_methods || profileData.is_public) && (
        <>
          {/* Favorite Strains Section */}
          {profileData.favorite_strains && profileData.favorite_strains.length > 0 && (
            <div style={{ marginBottom: '16px' }}>
              <h4 style={{ margin: '0 0 8px 0', fontSize: isMobile ? '12px' : '14px', color: '#bbb' }}>Favorite Strains</h4>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                {profileData.favorite_strains
                  .filter(strain => strain !== 'Choose your favorites') // Filter out placeholder
                  .map((strain, index) => (
                  <span key={index} style={{
                    padding: isMobile ? '3px 6px' : '4px 8px',
                    background: 'linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%)',
                    borderRadius: isMobile ? '6px' : '8px',
                    fontSize: isMobile ? '10px' : '12px',
                    color: '#059669',
                    border: '1px solid #10b981'
                  }}>
                    {strain}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Consumption Methods Section */}
          {profileData.consumption_methods && profileData.consumption_methods.length > 0 && (
            <div style={{ marginBottom: '16px' }}>
              <h4 style={{ margin: '0 0 8px 0', fontSize: isMobile ? '12px' : '14px', color: '#bbb' }}>Consumption Methods</h4>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                {profileData.consumption_methods.map((method, index) => (
                  <span key={index} style={{
                    padding: isMobile ? '3px 6px' : '4px 8px',
                    background: 'linear-gradient(135deg, #fef7ff 0%, #f3e8ff 100%)',
                    borderRadius: isMobile ? '6px' : '8px',
                    fontSize: isMobile ? '10px' : '12px',
                    color: '#7c3aed',
                    border: '1px solid #8b5cf6'
                  }}>
                    {method}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Experience and Tolerance */}
          <div style={{ 
            marginTop: '16px', 
            display: 'grid', 
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)', 
            gap: '12px' 
          }}>
            <div>
              <h4 style={{ margin: '0 0 4px 0', fontSize: isMobile ? '10px' : '12px', color: '#888' }}>Tolerance Level</h4>
              <div style={{
                padding: isMobile ? '4px 8px' : '6px 12px',
                background: colors.bgCard,
                borderRadius: isMobile ? '6px' : '8px',
                fontSize: isMobile ? '10px' : '12px',
                color: '#bbb',
                textTransform: 'capitalize'
              }}>
                {profileData.tolerance_level}
              </div>
            </div>
            <div>
              <h4 style={{ margin: '0 0 4px 0', fontSize: isMobile ? '10px' : '12px', color: '#888' }}>Experience</h4>
              <div style={{
                padding: isMobile ? '4px 8px' : '6px 12px',
                background: colors.bgCard,
                borderRadius: isMobile ? '6px' : '8px',
                fontSize: isMobile ? '10px' : '12px',
                color: '#bbb'
              }}>
                {profileData.years_of_experience} years
              </div>
            </div>
          </div>
        </>
      )}
      
      {!profileData.show_consumption_methods && !profileData.is_public && (
        <div style={{
          padding: isMobile ? '12px' : '16px',
          background: colors.bgCard,
          borderRadius: isMobile ? '8px' : '12px',
          textAlign: 'center',
          color: '#888',
          fontSize: isMobile ? '12px' : '14px'
        }}>
          <EyeOff size={isMobile ? 14 : 16} style={{ marginBottom: '8px' }} />
          <p style={{ margin: 0 }}>Cannabis preferences are private</p>
        </div>
      )}
    </div>
  );

   const RecentActivityCard = () => {
    const displayedActivities = showAllMode ? allActivities : recentActivity;

    return (
      <div style={{
        background: colors.bgTertiary,
        borderRadius: isMobile ? '16px' : '20px',
        padding: isMobile ? '16px' : '20px',
        border: '1px solid #333'
      }}>
        {/* Header with controls */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '16px',
          flexDirection: isMobile ? 'column' : 'row',
          gap: isMobile ? '12px' : '0'
        }}>
          <h3 style={{
            margin: 0,
            fontSize: isMobile ? '14px' : '16px',
            fontWeight: '600',
            color: '#e5e5e5',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <Activity size={isMobile ? 14 : 16} style={{ color: '#00d4aa' }} />
            {showAllMode ? 'All Activity' : 'Recent Activity'}
            {activityLoading && (
              <div style={{
                width: '12px',
                height: '12px',
                border: '2px solid #333',
                borderTop: '2px solid #00d4aa',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
                marginLeft: '8px'
              }} />
            )}
          </h3>

          {/* Controls - only show in All Mode */}
          {showAllMode && (
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '12px',
              flexDirection: isMobile ? 'column' : 'row',
              width: isMobile ? '100%' : 'auto'
            }}>
              {/* Limit Selector */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: isMobile ? '10px' : '12px', color: '#888' }}>Show:</span>
                <select
                  value={activityLimit}
                  onChange={(e) => handleLimitChange(parseInt(e.target.value))}
                  style={{
                    padding: isMobile ? '3px 6px' : '4px 8px',
                    background: colors.bgCard,
                    border: '1px solid #333',
                    borderRadius: isMobile ? '6px' : '8px',
                    color: '#e5e5e5',
                    fontSize: isMobile ? '10px' : '12px',
                    cursor: 'pointer'
                  }}
                >
                  <option value={10}>10 items</option>
                  <option value={15}>15 items</option>
                  <option value={25}>25 items</option>
                </select>
              </div>

              {/* Back to Overview */}
              <button
                onClick={handleBackToOverview}
                style={{
                  padding: isMobile ? '4px 8px' : '6px 12px',
                  background: 'transparent',
                  border: '1px solid #333',
                  borderRadius: isMobile ? '6px' : '8px',
                  color: '#888',
                  cursor: 'pointer',
                  fontSize: isMobile ? '10px' : '12px',
                  fontWeight: '500',
                  transition: 'all 0.3s ease',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = '#00d4aa';
                  e.currentTarget.style.color = '#00d4aa';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = '#333';
                  e.currentTarget.style.color = '#888';
                }}
              >
                ← Overview
              </button>
            </div>
          )}
        </div>

        {/* Activity Count Info - only show in All Mode */}
        {showAllMode && totalActivities > 0 && (
          <div style={{
            marginBottom: '16px',
            padding: isMobile ? '6px 8px' : '8px 12px',
            background: colors.bgCard,
            borderRadius: isMobile ? '6px' : '8px',
            fontSize: isMobile ? '10px' : '12px',
            color: '#888'
          }}>
            Showing {displayedActivities.length} of {totalActivities} activities
          </div>
        )}
        
        {/* Activity List */}
        {activityLoading && displayedActivities.length === 0 ? (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '12px'
          }}>
            {[...Array(4)].map((_, index) => (
              <div key={index} style={{
                padding: isMobile ? '8px' : '12px',
                background: 'linear-gradient(135deg, #2a2a2a 0%, #1f1f1f 100%)',
                borderRadius: isMobile ? '8px' : '12px',
                border: '1px solid #333',
                opacity: 0.5
              }}>
                <div style={{
                  height: isMobile ? '12px' : '16px',
                  background: '#333',
                  borderRadius: '4px',
                  marginBottom: '8px',
                  width: '80%'
                }} />
                <div style={{
                  height: isMobile ? '8px' : '12px',
                  background: '#333',
                  borderRadius: '4px',
                  width: '60%'
                }} />
              </div>
            ))}
          </div>
        ) : displayedActivities.length === 0 ? (
          <div style={{
            textAlign: 'center',
            padding: isMobile ? '24px 12px' : '32px 16px',
            color: '#888'
          }}>
            <Activity size={isMobile ? 24 : 32} style={{ marginBottom: '12px', opacity: 0.5 }} />
            <p style={{ margin: 0, fontSize: isMobile ? '12px' : '14px' }}>
              No recent activity yet
            </p>
            <p style={{ margin: '4px 0 0 0', fontSize: isMobile ? '10px' : '12px' }}>
              Start participating in discussions!
            </p>
          </div>
        ) : (
          <>
            <div style={{ display: 'flex', flexDirection: 'column', gap: isMobile ? '8px' : '12px' }}>
              {displayedActivities.map((activity, index) => (
                <EnhancedActivityCard
                  key={`${activity.type}-${activity.id}-${index}`}
                  activity={activity}
                  colors={colors}
                  formatTimeAgo={formatTimeAgo}
                  getSmokerEmoji={getSmokerEmoji}
                  onVote={(postId, direction) => {
                    console.log('Vote:', postId, direction);
                  }}
                  onBookmark={(postId) => {
                    console.log('Bookmark:', postId);
                  }}
                  onShare={(activity) => {
                    console.log('Share:', activity.title);
                  }}
                />
              ))}
            </div>

            {/* Load More / View All Controls */}
            <div style={{
              marginTop: '16px',
              textAlign: 'center',
              padding: isMobile ? '8px 0' : '12px 0',
              borderTop: '1px solid #333'
            }}>
              {!showAllMode ? (
                // Initial "View All Activity" button
                <button
                  onClick={handleViewAllActivity}
                  style={{
                    padding: isMobile ? '6px 12px' : '8px 16px',
                    background: 'linear-gradient(135deg, #00d4aa20 0%, #10b98120 100%)',
                    border: '1px solid #00d4aa40',
                    borderRadius: isMobile ? '8px' : '12px',
                    color: '#00d4aa',
                    cursor: 'pointer',
                    fontSize: isMobile ? '10px' : '12px',
                    fontWeight: '500',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'linear-gradient(135deg, #00d4aa30 0%, #10b98130 100%)';
                    e.currentTarget.style.borderColor = '#00d4aa';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'linear-gradient(135deg, #00d4aa20 0%, #10b98120 100%)';
                    e.currentTarget.style.borderColor = '#00d4aa40';
                  }}
                >
                  View All Activity →
                </button>
              ) : (
                // Load More button for expanded view
                hasMoreActivities && (
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px' }}>
                    <button
                      onClick={handleLoadMore}
                      disabled={activityLoading}
                      style={{
                        padding: isMobile ? '6px 12px' : '8px 16px',
                        background: activityLoading 
                          ? 'rgba(0, 212, 170, 0.1)' 
                          : 'linear-gradient(135deg, #00d4aa20 0%, #10b98120 100%)',
                        border: '1px solid #00d4aa40',
                        borderRadius: isMobile ? '8px' : '12px',
                        color: activityLoading ? '#888' : '#00d4aa',
                        cursor: activityLoading ? 'not-allowed' : 'pointer',
                        fontSize: isMobile ? '10px' : '12px',
                        fontWeight: '500',
                        transition: 'all 0.3s ease',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}
                      onMouseEnter={(e) => {
                        if (!activityLoading) {
                          e.currentTarget.style.background = 'linear-gradient(135deg, #00d4aa30 0%, #10b98130 100%)';
                          e.currentTarget.style.borderColor = '#00d4aa';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!activityLoading) {
                          e.currentTarget.style.background = 'linear-gradient(135deg, #00d4aa20 0%, #10b98120 100%)';
                          e.currentTarget.style.borderColor = '#00d4aa40';
                        }
                      }}
                    >
                      {activityLoading && (
                        <div style={{
                          width: '12px',
                          height: '12px',
                          border: '2px solid #333',
                          borderTop: '2px solid #00d4aa',
                          borderRadius: '50%',
                          animation: 'spin 1s linear infinite'
                        }} />
                      )}
                      {activityLoading ? 'Loading...' : 'Load More'}
                    </button>
                    
                    {/* Pagination info */}
                    <div style={{
                      fontSize: isMobile ? '9px' : '11px',
                      color: '#666',
                      textAlign: 'center'
                    }}>
                      {displayedActivities.length} of {totalActivities} loaded
                    </div>
                  </div>
                )
              )}
            </div>
          </>
        )}
      </div>
    );
  };


  if (loading || !profileData) {
    return (
      <div style={{
        minHeight: '100vh',
        backgroundColor: '#0a0a0a',
        color: 'white',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: isMobile ? '32px' : '40px',
            height: isMobile ? '32px' : '40px',
            border: '4px solid #333',
            borderTop: '4px solid #00d4aa',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 16px'
          }} />
          <p style={{ margin: 0, color: '#888', fontSize: isMobile ? '14px' : '16px' }}>Loading profile...</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: '#0a0a0a',
      color: 'white',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
    }}>
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
      
      <div style={{ 
        maxWidth: '1200px', 
        margin: '0 auto', 
        padding: isMobile ? '12px' : '24px' 
      }}>
        {isEditing ? (
          <EditProfile 
            formData={formData}          // Initial data only
            isMockProfile={isMockProfile}
            onSaveSuccess={(savedData) => {
              // Handle successful save
              setProfileData(savedData);
              setIsEditing(false);
            }}
            handleCancel={() => setIsEditing(false)}
          />
        ) : (
          <>
            <ProfileHeader />
            
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: isMobile ? '1fr' : '1fr 2fr', 
              gap: isMobile ? '20px' : '32px' 
            }}>
              {/* Left Sidebar */}
              <div>
                <StatsCard />
                <AchievementsCard 
                  profileData={profileData} 
                  loading={achievementsLoading} 
                  isAuthenticated={isAuthenticated} 
                />
                <PreferencesCard />
              </div>

              {/* Main Content */}
              <div>
                <RecentActivityCard />
              </div>
            </div>
          </>
        )}
      </div>
      <CoverImageUploadDialog
        isOpen={uploadDialogOpen}
        onClose={() => setUploadDialogOpen(false)}
        onUploadSuccess={handleCoverImageSuccess}
        currentImageUrl={profileData.cover_image_url}
      />
      <BackgroundColorSelector
        isOpen={dialogOpen}
        onClose={() => setDialogOpen(false)}
        currentLevel={profileData?.level || 1}
        selectedColor={selectedColor}
        onColorSelect={handleColorSelect}
      />
     <AvatarOnboardingDialog
        isOpen={avatarSelectorOpen}
        onClose={() => setAvatarSelectorOpen(false)}
        onAvatarSelect={handleAvatarSelect}
        API_URL={API_URL}
        userName={profileData?.display_name || profileData?.username || ""}
        isOnboarding={false} // This skips welcome and goes directly to avatar selection
        currentAvatarUrl={profileData?.user_avatar || "https://highmind-bucket.s3.us-east-1.amazonaws.com/6d4ba06007624c3691fd682dfbb31cdf_hmind.png"}
      />
    </div>
  );
};

export default ProfilePage;